import React, { useContext } from 'react';

import Slider from '@material-ui/core/Slider';
import {
	FormControlLabel,
	FormGroup,
	makeStyles,
	Switch,
	Tooltip,
} from '@material-ui/core';

import './FiltersContainer.style.css';

import InputWrapper from '../InputWrapper/InputWrapper.component';
import { defaultState, FilterContext } from '../../contexts/FilterContext';
import { ReactComponent as TooltipModalIcon } from '../../assets/modal-icon.svg';

export interface FiltersContainerProps {
	isHidden: boolean;
}

const useStyles = makeStyles(() => ({
	customTooltip: {
		backgroundColor: '#f0f2f5',
		color: '#000',
		fontSize: '1rem',
		padding: '1rem',
	},
	customArrow: {
		color: '#f0f2f5',
		width: '2em',
		height: '1.42em',
		marginBottom: '-1.3em !important',
	},
}));

const FiltersContainer: React.FC<FiltersContainerProps> = ({ isHidden }) => {
	const [filters, setFilters] = useContext(FilterContext);
	const {
		graphViewmode,
		corpus,
		freq,
		lang,
		case_sens,
		isCumulative,
		limit,
		leaves,
		searchTerms,
	} = filters;
	const classes = useStyles();

	const handleRadioToggle = (event: any) => {
		setFilters({
			...defaultState,
			[event.target.name]: event.target.id,
			searchTerms,
		});
	};

	const handleCorpusRadioToggle = (event: any) => {
		switch (event.target.id) {
			case 'paper-nor':
				setFilters({ ...filters, lang: 'nor', corpus: ['avis'] });
				break;
			case 'book-nor':
				setFilters({ ...filters, lang: 'nor', corpus: ['bok'] });
				break;
			case 'book-nno':
				setFilters({ ...filters, lang: 'nno', corpus: ['bok'] });
				break;
			case 'book-nob':
				setFilters({ ...filters, lang: 'nob', corpus: ['bok'] });
				break;
			case 'book-sme':
				setFilters({ ...filters, lang: 'sme', corpus: ['bok'] });
				break;
			case 'book-smj':
				setFilters({ ...filters, lang: 'smj', corpus: ['bok'] });
				break;
			case 'book-sma':
				setFilters({ ...filters, lang: 'sma', corpus: ['bok'] });
				break;
			case 'book-fkv':
				setFilters({ ...filters, lang: 'fkv', corpus: ['bok'] });
				break;
			case 'all-nob':
				setFilters({ ...filters, lang: 'nob', corpus: ['all'] });
				break;
		}
	};

	const handleGalaxyRadioToggle = (event: any) => {
		if (event.target.id === 'low') {
			setFilters({ ...filters, limit: 12 });
		} else if (event.target.id === 'medium') {
			setFilters({ ...filters, limit: 16 });
		} else {
			setFilters({ ...filters, limit: 20 });
		}
	};

	return (
		<div
			aria-hidden={isHidden}
			tabIndex={isHidden ? -1 : undefined}
			className={`filters-container ${isHidden && 'is-hidden'}`}
		>
			<InputWrapper inputWrapperLabel="GRAFISK FREMSTILLING">
				<form onChange={handleRadioToggle}>
					<div className="input-container">
						<input
							type="radio"
							name="graphViewmode"
							id="trendlinjer"
							checked={graphViewmode === 'trendlinjer'}
						/>
						<label htmlFor="trendlinjer">Trendlinjer</label>
						<div className="modal-icon-wrapper">
							<Tooltip
								title="Lar deg undersøke og sammelikne utviklingen av ord og fraser over tid."
								arrow
								placement="top"
								classes={{
									tooltip: classes.customTooltip,
									arrow: classes.customArrow,
								}}
							>
								<TooltipModalIcon />
							</Tooltip>
						</div>
					</div>
					<div className="input-container">
						<input
							type="radio"
							name="graphViewmode"
							id="råfrekvens"
							checked={graphViewmode === 'råfrekvens'}
						/>
						<label htmlFor="råfrekvens">Råfrekvenser</label>
						<Tooltip
							title="Gir deg statistikk over ord og fraser i hele korpuset uavhengig av tid."
							arrow
							placement="top"
							classes={{
								tooltip: classes.customTooltip,
								arrow: classes.customArrow,
							}}
						>
							<div className="modal-icon-wrapper">
								<TooltipModalIcon />
							</div>
						</Tooltip>
					</div>
					<div className="input-container">
						<input
							type="radio"
							name="graphViewmode"
							id="galakser"
							checked={graphViewmode === 'galakser'}
						/>
						<label htmlFor="galakser">Galakser</label>
						<Tooltip
							title="Gir deg muligheten til å se på betydningsreleasjoner mellom ord."
							arrow
							placement="top"
							classes={{
								tooltip: classes.customTooltip,
								arrow: classes.customArrow,
							}}
						>
							<div className="modal-icon-wrapper">
								<TooltipModalIcon />
							</div>
						</Tooltip>
					</div>
				</form>
			</InputWrapper>

			{graphViewmode === 'trendlinjer' && (
				<>
					{!isCumulative && (
						<InputWrapper
							inputWrapperLabel="GLATTING"
							tooltipModalText="Kurven jevnes ut basert på gjennomsnittet av omkringliggende punkt"
						>
							<Slider
								valueLabelDisplay="auto"
								min={0}
								max={20}
								value={filters.smoothing}
								onChange={(event, newValue) =>
									setFilters({
										...filters,
										smoothing: newValue,
									})
								}
							/>
							<div className="slider-range-label">
								<span>0</span>
								<span>20</span>
							</div>
						</InputWrapper>
					)}

					<InputWrapper
						inputWrapperLabel="Målform/Korpus"
						tooltipModalText="Velger materialtype og bokmål og/eller nynorsk"
					>
						<form onChange={handleCorpusRadioToggle}>
							<div className="input-container">
								<input
									type="radio"
									name="lang"
									id="paper-nor"
									checked={
										lang === 'nor' && corpus[0] === 'avis'
									}
								/>
								<label htmlFor="paper-nor">Avis</label>
							</div>
							<div className="input-container">
								<input
									type="radio"
									name="lang"
									id="book-nor"
									checked={
										lang === 'nor' && corpus[0] === 'bok'
									}
								/>
								<label htmlFor="book-nor">
									Bok- alle målformer
								</label>
							</div>
							<div className="input-container">
								<input
									type="radio"
									name="lang"
									id="book-nno"
									checked={
										lang === 'nno' && corpus[0] === 'bok'
									}
								/>
								<label htmlFor="book-nno">Bok- nynorsk</label>
							</div>
							<div className="input-container">
								<input
									type="radio"
									name="lang"
									id="book-nob"
									checked={
										lang === 'nob' && corpus[0] === 'bok'
									}
								/>
								<label htmlFor="book-nob">Bok- bokmål</label>
							</div>
							<div className="input-container">
								<input
									type="radio"
									name="lang"
									id="book-sme"
									checked={
										lang === 'sme' && corpus[0] === 'bok'
									}
								/>
								<label htmlFor="book-sme">
									Bok- nordsamisk
								</label>
							</div>
							<div className="input-container">
								<input
									type="radio"
									name="lang"
									id="book-smj"
									checked={
										lang === 'smj' && corpus[0] === 'bok'
									}
								/>
								<label htmlFor="book-smj">
									Bok- lulesamisk
								</label>
							</div>
							<div className="input-container">
								<input
									type="radio"
									name="lang"
									id="book-sma"
									checked={
										lang === 'sma' && corpus[0] === 'bok'
									}
								/>
								<label htmlFor="book-sma">Bok- sørsamisk</label>
							</div>
							<div className="input-container">
								<input
									type="radio"
									name="lang"
									id="book-fkv"
									checked={
										lang === 'fkv' && corpus[0] === 'bok'
									}
								/>
								<label htmlFor="book-fkv">Bok- kvensk</label>
							</div>
						</form>
					</InputWrapper>

					<InputWrapper inputWrapperLabel="Frekvens">
						<form onChange={handleRadioToggle}>
							<div className="input-container">
								<input
									type="radio"
									name="freq"
									id="rel"
									checked={freq === 'rel'}
								/>
								<label htmlFor="rel">Vis relativfrekvens</label>
								<Tooltip
									title="Treff som prosent av antall ord per år."
									arrow
									placement="top"
									classes={{
										tooltip: classes.customTooltip,
										arrow: classes.customArrow,
									}}
								>
									<div className="modal-icon-wrapper">
										<TooltipModalIcon />
									</div>
								</Tooltip>
							</div>
							<div className="input-container">
								<input
									type="radio"
									name="freq"
									id="abs"
									checked={freq === 'abs'}
								/>
								<label htmlFor="abs">
									Vis absoluttfrekvens
								</label>
								<Tooltip
									title="Antallet treff innenfor et år (råfrekvens)."
									arrow
									placement="top"
									classes={{
										tooltip: classes.customTooltip,
										arrow: classes.customArrow,
									}}
								>
									<div className="modal-icon-wrapper">
										<TooltipModalIcon />
									</div>
								</Tooltip>
							</div>
						</form>
					</InputWrapper>

					<InputWrapper inputWrapperLabel="Annet">
						<FormGroup>
							<FormControlLabel
								control={
									<Switch
										checked={isCumulative}
										onChange={() =>
											setFilters({
												...filters,
												isCumulative: !isCumulative,
											})
										}
									/>
								}
								label="Vis kumulativ graf"
							/>
							<FormControlLabel
								control={
									<Switch
										checked={!!case_sens}
										onChange={() =>
											setFilters({
												...filters,
												case_sens: case_sens ? 0 : 1,
											})
										}
									/>
								}
								label="Skill mellom stor og liten bokstav"
							/>
						</FormGroup>
					</InputWrapper>
				</>
			)}
			{graphViewmode === 'råfrekvens' && (
				<InputWrapper
					inputWrapperLabel="Målform/Korpus"
					tooltipModalText="Velger materialtype og bokmål og/eller nynorsk"
				>
					<form onChange={handleCorpusRadioToggle}>
						<div className="input-container">
							<input
								type="radio"
								name="lang"
								id="paper-nor"
								checked={lang === 'nor' && corpus[0] === 'avis'}
							/>
							<label htmlFor="paper-nor">Avis</label>
						</div>
						<div className="input-container">
							<input
								type="radio"
								name="lang"
								id="book-nor"
								checked={lang === 'nor' && corpus[0] === 'bok'}
							/>
							<label htmlFor="book-nor">
								Bok- alle målformer
							</label>
						</div>
						<div className="input-container">
							<input
								type="radio"
								name="lang"
								id="book-nno"
								checked={lang === 'nno' && corpus[0] === 'bok'}
							/>
							<label htmlFor="book-nno">Bok- nynorsk</label>
						</div>
						<div className="input-container">
							<input
								type="radio"
								name="lang"
								id="book-nob"
								checked={lang === 'nob' && corpus[0] === 'bok'}
							/>
							<label htmlFor="book-nob">Bok- bokmål</label>
						</div>
						<div className="input-container">
							<input
								type="radio"
								name="lang"
								id="book-sme"
								checked={lang === 'sme' && corpus[0] === 'bok'}
							/>
							<label htmlFor="book-sme">Bok- nordsamisk</label>
						</div>
						<div className="input-container">
							<input
								type="radio"
								name="lang"
								id="book-smj"
								checked={lang === 'smj' && corpus[0] === 'bok'}
							/>
							<label htmlFor="book-smj">Bok- lulesamisk</label>
						</div>
						<div className="input-container">
							<input
								type="radio"
								name="lang"
								id="book-sma"
								checked={lang === 'sma' && corpus[0] === 'bok'}
							/>
							<label htmlFor="book-sma">Bok- sørsamisk</label>
						</div>
						<div className="input-container">
							<input
								type="radio"
								name="lang"
								id="book-fkv"
								checked={lang === 'fkv' && corpus[0] === 'bok'}
							/>
							<label htmlFor="book-fkv">Bok- kvensk</label>
						</div>
					</form>
				</InputWrapper>
			)}
			{graphViewmode === 'galakser' && (
				<>
					<InputWrapper
						inputWrapperLabel="Målform/Korpus"
						tooltipModalText="Velger materialtype og bokmål og/eller nynorsk"
					>
						<form onChange={handleCorpusRadioToggle}>
							<div className="input-container">
								<input
									type="radio"
									name="lang"
									id="all-nob"
									checked={
										lang === 'nob' && corpus[0] === 'all'
									}
								/>
								<label htmlFor="all-nob">Avis og bok</label>
							</div>
							<div className="input-container">
								<input
									type="radio"
									name="lang"
									id="paper-nor"
									checked={
										lang === 'nor' && corpus[0] === 'avis'
									}
								/>
								<label htmlFor="paper-nor">Avis</label>
							</div>
							<div className="input-container">
								<input
									type="radio"
									name="lang"
									id="book-nor"
									checked={
										lang === 'nor' && corpus[0] === 'bok'
									}
								/>
								<label htmlFor="book-nor">Bok</label>
							</div>
						</form>
					</InputWrapper>
					<InputWrapper
						inputWrapperLabel="Tetthet"
						tooltipModalText="Indikerer størrelse på grafen"
					>
						<form onChange={handleGalaxyRadioToggle}>
							<div className="input-container">
								<input
									type="radio"
									name="limit"
									id="low"
									checked={limit === 12}
								/>
								<label htmlFor="low">Lav</label>
							</div>
							<div className="input-container">
								<input
									type="radio"
									name="limit"
									id="medium"
									checked={limit === 16}
								/>
								<label htmlFor="medium">Middels</label>
							</div>
							<div className="input-container">
								<input
									type="radio"
									name="limit"
									id="high"
									checked={limit === 20}
								/>
								<label htmlFor="high">Høy</label>
							</div>
						</form>
					</InputWrapper>
					<InputWrapper inputWrapperLabel="Annet">
						<FormGroup>
							<FormControlLabel
								control={
									<Switch
										checked={leaves}
										onChange={() =>
											setFilters({
												...filters,
												leaves: !leaves,
											})
										}
									/>
								}
								label="Vis bladnoder"
							/>
							<Tooltip
								title="Noder som ikke peker videre"
								arrow
								placement="top"
								classes={{
									tooltip: classes.customTooltip,
									arrow: classes.customArrow,
								}}
							>
								<div className="modal-icon-wrapper">
									<TooltipModalIcon />
								</div>
							</Tooltip>
						</FormGroup>
					</InputWrapper>
				</>
			)}
		</div>
	);
};

export default FiltersContainer;
